import { useSignal } from "@preact/signals-react";
import { useEffect, useRef, forwardRef } from "react";
import { FormattedMessage as FM } from "react-intl";
import { addMonths } from "date-fns";
import { Spanish } from "flatpickr/dist/l10n/es";

import { useStore } from "storev2/formStore";
import { CalendarIcon, EditIcon } from "components/Icons/linear";
import { ChevronDownIcon, XMarkIcon } from "components/Icons";
import EventCard from "./EventCard";
import HoursDay from "./HoursDay";
import AvailableTimeCard from "./AvailableTimeCard";
import useLangStore from "store/langStore";
import dates from "componentsv2/tools/dates";
import { classNames } from "tools/ui";
import utils from "../services/appointment";
import { useContext, setTimeData } from "../store/useContext";
import BoardForm from "componentsv2/form/BoardForm";
import { layout as layoutSchedule } from "../models/ModelScheduleDayView";
import { genStoreId } from "componentsv2/tools/functions";

function DaysHeader({ newDays, onClickDay, currentDate }, ref) {
  const fmCalendar = Spanish;
  const days = fmCalendar.weekdays.shorthand;
  const dateString = dates.fmt(currentDate, "yyyy-MM-dd");

  return (
    <div className="sticky top-0 z-30 ">
      <div
        ref={ref}
        className="grid flex-none grid-cols-7 bg-gray-100 text-xs text-gray-500 ring-1 shadow-sm ring-black/5 md:hidden"
      >
        {newDays.map((day) => (
          <button
            key={day.dayString}
            id={day.dayString}
            type="button"
            onClick={() => onClickDay(day)}
            className="flex flex-col items-center pt-3 pb-1.5"
          >
            {days.find((d) => d[0] === day.dayString)?.[0] ?? ""}
            <span
              key={day.dayString}
              className={classNames(
                "mt-3 flex size-8 items-center justify-center rounded-full text-base font-semibold ",
                day.isToday && day.dayString !== dateString
                  ? " text-indigo-600"
                  : day.isToday
                    ? " rounded-full bg-indigo-600  text-white"
                    : day.dayString === dateString
                      ? " rounded-full bg-gray-900  text-white"
                      : "text-gray-900",
              )}
            >
              {dates.fmt(day.day, "d")}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function OwnersHeader() {
  const { context } = useContext();
  const { owners, rooms, configuration } = context;

  const type = configuration?.[0]?.default_scheduling_mode;
  const resources = type === "room" ? rooms : owners;
  const columns = resources.length;

  return (
    <div className="sticky top-0 z-30 hidden  md:flex flex-row bg-gray-100 py-2 w-fit">
      <div className="w-14 flex-none ring-1 ring-gray-100" />
      <div
        className="col-start-1 col-end-2 row-start-1 hidden grid-rows-1 divide-x divide-white md:grid"
        style={{ gridTemplateColumns: `repeat(${columns}, 250px)` }}
      >
        {resources.map((item) => (
          <div key={item.id}>
            <h3 className="text-center font-bold text-gray-700 capitalize">
              {(item?.name || item?.rec_name)?.toLowerCase()}
            </h3>
          </div>
        ))}
      </div>
    </div>
  );
}

const ForwardedHoursDay = forwardRef(HoursDay);
const ForwardedDaysHeader = forwardRef(DaysHeader);
const ForwardedOwnersHeader = forwardRef(OwnersHeader);

export default function DayView({ newDays, handleDay, handleEvent }) {
  const { context, updateContext } = useContext();
  const { newRecord } = useStore();

  const { owners, rooms, currentDate, availableTime, configuration } = context;

  const viewMode = useSignal("calendar");
  const dayViewRecordStoreId = useSignal(null);
  const sidebarOpen = useSignal(true);

  const type = configuration?.[0]?.default_scheduling_mode;
  const resources = type === "room" ? rooms : owners;
  const container = useRef(null);
  const containerNav = useRef(null);
  const containerOffset = useRef(null);
  const { locale } = useLangStore();
  const fmCalendar = Spanish;
  const days = fmCalendar.weekdays.shorthand;
  const cdate = dates.fmt(currentDate, "yyyy-MM-dd");
  const isSingleColumn = window.innerWidth < 768;

  const columns = isSingleColumn ? 1 : resources.length;
  const templateColumns = isSingleColumn ? "" : `repeat(${columns}, 250px)`;

  const availableTime_ = availableTime.filter((time) => time.date === cdate);

  const newDays_ = newDays.filter((day) => day.dayString === cdate);

  const { startHour, endHour } = utils.getHourRange(
    context.minStartTime,
    context.maxEndTime,
  );

  const startOfWeek = dates.startOfWeek(currentDate);
  const endOfWeek = dates.endOfWeek(currentDate);

  const daysWeek = newDays.filter((event) => {
    const eventDate = new Date(event.day);
    return eventDate >= startOfWeek && eventDate <= endOfWeek;
  });

  const onChangedate = async (direction) => {
    const diff = direction === "previous" ? -1 : 1;
    updateContext({ currentDate: addMonths(currentDate, diff) });
    await setTimeData();
  };
  function normalizeEvent(event) {
    if (event.start_date && event.end_date) return event;
    if (event.appointment_date) {
      const start = new Date(event.appointment_date);
      start.setHours(9, 0, 0, 0); // hora por defecto
      const end = new Date(event.appointment_date);
      end.setHours(10, 0, 0, 0); // duración 1h

      return {
        ...event,
        start_date: start.toISOString(),
        end_date: end.toISOString(),
      };
    }
    return null;
  }
  function onClickDay(day) {
    updateContext({ currentDate: day.day });
  }
  function getEvents(e, day) {
    e.stopPropagation();
    e.preventDefault();
    updateContext({ currentDate: day.day });
  }
  function handleClick(e) {
    const olElement = e.currentTarget.closest("ol");
    if (!olElement) return;
    const calendar = olElement;
    const rect = calendar.getBoundingClientRect();
    const scrollTop = calendar.scrollTop;
    const scrollLeft = calendar.scrollLeft;
    const relativeY = e.clientY - rect.top + scrollTop;
    const relativeX = e.clientX - rect.left + scrollLeft;
    const rowHeight = (rect.height - 28) / ((endHour - startHour) * 12);
    const gridRow = Math.floor(relativeY / rowHeight) + 2;
    const totalMinutes = Math.floor((gridRow - 4) * 5) + startHour * 60;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const columnWidth = rect.width / columns;
    const index = Math.floor(relativeX / columnWidth);
    const resource = resources[index];
    const day = new Date(currentDate);
    day.setHours(hours);
    day.setMinutes(minutes);
    handleDay(e, day, day, resource);
  }

  useEffect(() => {
    updateContext({ currentOwner: null, currentRoom: null });
    setTimeData();
  }, []);
  useEffect(() => {
    const currentMinute = new Date().getHours() * 60;
    container.current.scrollTop =
      ((container.current.scrollHeight -
        containerNav.current.offsetHeight -
        containerOffset.current.offsetHeight) *
        currentMinute) /
      1440;
  }, []);

  return (
    <div className="flex max-h-[calc(100vh-7rem)] w-full flex-col">
      <div className="isolate flex flex-auto overflow-hidden bg-white">
        <div
          ref={container}
          className="flex flex-auto flex-col overflow-auto scroll-appointment w-fit agenda-container"
        >
          <ForwardedDaysHeader
            ref={containerNav}
            newDays={daysWeek}
            onClickDay={onClickDay}
            currentDate={currentDate}
          />
          {isSingleColumn ? null : <ForwardedOwnersHeader />}
          <div className="flex flex-none w-full">
            <div className="w-14 flex-none bg-white ring-1 ring-gray-100" />
            <div className="grid">
              <ForwardedHoursDay ref={containerOffset} />
              {!isSingleColumn && (
                <div
                  className="col-start-1 col-end-2 row-start-1 hidden grid-rows-1 divide-x divide-gray-100 md:grid"
                  style={{ gridTemplateColumns: `repeat(${columns}, 250px)` }}
                >
                  {resources.map((item) => (
                    <div key={item.id} />
                  ))}
                </div>
              )}
              {/* Available Time */}
              <ol
                role="grid"
                className="col-start-1 col-end-2 row-start-1 grid grid-cols-1"
                style={{
                  gridTemplateRows: `1.75rem repeat(${(endHour - startHour) * 12}, minmax(0, 1fr)) auto`,
                  gridTemplateColumns: templateColumns,
                }}
              >
                {availableTime_.map((time) => {
                  const resourceId =
                    type === "room"
                      ? Array.isArray(time.room)
                        ? time.room[0]
                        : time.room
                      : Array.isArray(time.owner)
                        ? time.owner[0]
                        : time.owner;

                  return (
                    <AvailableTimeCard
                      key={time.id}
                      time={time}
                      handleClick={handleClick}
                      col={resources.findIndex((r) => r.id === resourceId) + 1}
                    />
                  );
                })}
              </ol>

              {/* Events */}
              <div
                role="grid"
                className="col-start-1 col-end-2 row-start-1 grid grid-cols-1"
                style={{
                  gridTemplateRows: `1.75rem repeat(${(endHour - startHour) * 12}, minmax(0, 1fr)) auto`,
                  gridTemplateColumns: templateColumns,
                }}
              >
                {newDays_.map((day) =>
                  day.events
                    .map(normalizeEvent)
                    .filter(Boolean)
                    .map((event) => {
                      const resourceId =
                        type === "room" ? event.item?.room : event.owner_id;

                      const colIndex = resources.findIndex(
                        (r) => r.id === resourceId,
                      );

                      const safeColIndex = colIndex === -1 ? 0 : colIndex;

                      return (
                        <EventCard
                          key={event.id}
                          event={event}
                          handleEvent={handleEvent}
                          view="day"
                          isSingleColumn={isSingleColumn}
                          col={safeColIndex + 1}
                        />
                      );
                    }),
                )}
              </div>
            </div>
          </div>
        </div>
        {sidebarOpen.value && (
          <div className="hidden w-1/4 flex-none border-l border-gray-100 px-4 py-5 md:flex md:flex-col overflow-hidden max-h-full">
            <div className="flex-none">
              <div className="flex justify-end ">
                <button
                  type="button"
                  onClick={() => {
                    sidebarOpen.value = false;
                  }}
                  className="text-xl flex justify-around "
                >
                  <XMarkIcon className="w-7 fill-current text-neutral-500 hover:text-red-600 active:text-blue-300" />
                </button>
              </div>
            </div>
            <div className="text-xl flex justify-around p-4">
              <button
                type="button"
                onClick={() => {
                  viewMode.value = "calendar";
                  dayViewRecordStoreId.value = null;
                  setTimeData();
                }}
                className={classNames(
                  "px-3 py-1 rounded-md",
                  viewMode.value === "calendar"
                    ? "bg-indigo-600 text-gray-50"
                    : "text-gray-500 hover:text-gray-800",
                )}
              >
                <FM id={"calendar_appointment"} />
              </button>
              <button
                type="button"
                onClick={async () => {
                  viewMode.value = "schedule";
                  const _layout = layoutSchedule();
                  const storeId = await newRecord(_layout, null);
                  dayViewRecordStoreId.value = storeId;
                }}
                className={classNames(
                  "px-3 py-1 rounded-md",
                  viewMode.value === "schedule"
                    ? "bg-indigo-600 text-white"
                    : "text-gray-500 hover:text-gray-700",
                )}
              >
                <FM id={"schedule_appointment"} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto min-h-0">
              {viewMode.value === "schedule" && (
                <ScheduleForm
                  layoutSchedule={layoutSchedule}
                  storeId={dayViewRecordStoreId.value}
                  dayViewRecordStoreId={dayViewRecordStoreId}
                  viewMode={viewMode}
                />
              )}

              {viewMode.value === "calendar" && (
                <>
                  <div className="flex items-center text-center text-gray-900">
                    <button
                      type="button"
                      onClick={() => onChangedate("previous")}
                      className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
                    >
                      <span className="sr-only">
                        <FM id={"previous_month"} />
                      </span>
                      <ChevronDownIcon
                        className="size-5 rotate-90"
                        aria-hidden="true"
                      />
                    </button>
                    <div className="flex-auto text-sm font-semibold">
                      {currentDate.toLocaleDateString(locale, {
                        month: "long",
                        year: "numeric",
                      })}
                    </div>
                    <button
                      type="button"
                      onClick={() => onChangedate("next")}
                      className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
                    >
                      <span className="sr-only">
                        <FM id={"next_month"} />
                      </span>
                      <ChevronDownIcon
                        className="size-5 -rotate-90"
                        aria-hidden="true"
                      />
                    </button>
                  </div>

                  <div className="mt-6 grid grid-cols-7 text-center text-xs/6 text-gray-500">
                    {fmCalendar?.weekdays?.shorthand?.map((day) => (
                      <div key={day} className="bg-white py-2">
                        <span className="sr-only">{day}</span>
                        {day[0]}
                      </div>
                    ))}
                  </div>

                  <div className="isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-gray-200 text-sm ring-1 shadow-sm ring-gray-200">
                    {newDays.map((day, dayIdx) => (
                      <button
                        key={day.date}
                        type="button"
                        onClick={(e) => getEvents(e, day)}
                        className={classNames(
                          "py-1.5 hover:bg-gray-100 focus:z-10",
                          day.isCurrentMonth ? "bg-white" : "bg-gray-50",
                          (day.isSelected || day.isToday) && "font-semibold",
                          day.isSelected && "text-white",
                          !day.isSelected &&
                            day.isCurrentMonth &&
                            !day.isToday &&
                            "text-gray-900",
                          !day.isSelected &&
                            !day.isCurrentMonth &&
                            !day.isToday &&
                            "text-gray-400",
                          day.isToday && !day.isSelected && "text-indigo-600",
                        )}
                      >
                        <time
                          dateTime={day.date}
                          className={classNames(
                            "mx-auto flex size-7 items-center justify-center rounded-full",
                            day.isSelected && day.isToday && "bg-indigo-600",
                            day.isSelected && !day.isToday && "bg-gray-900",
                          )}
                        >
                          {dates.fmt(day.day, "d")}
                        </time>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        {!sidebarOpen.value && (
          <div className="flex flex-col gap-1 border-l border-gray-100 p-2 self-start mt-2 justify-center center space-y-4">
            <button
              type="button"
              onClick={() => {
                viewMode.value = "calendar";
                sidebarOpen.value = true;
                setTimeData();
              }}
              className="text-gray-600 hover:text-indigo-600 hover:bg-gray-100 p-1.5 rounded text-xs font-semibold leading-none"
              title="Calendar"
            >
              <CalendarIcon />
            </button>
            <button
              type="button"
              onClick={async () => {
                viewMode.value = "schedule";
                sidebarOpen.value = true;
                const _layout = layoutSchedule();
                const storeId = await newRecord(_layout, null);
                dayViewRecordStoreId.value = storeId;
              }}
              className="text-gray-600 hover:text-indigo-600 hover:bg-gray-100 p-1.5 rounded text-xs font-semibold leading-none"
              title="schedule_appointment"
            >
              <EditIcon />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ScheduleForm({
  layoutSchedule,
  storeId: propStoreId,
  dayViewRecordStoreId,
  viewMode,
}) {
  const recordStoreId = useSignal(propStoreId ?? null);

  useEffect(() => {
    if (propStoreId) recordStoreId.value = propStoreId;
  }, [propStoreId]);

  const _layoutSchedule = layoutSchedule();

  function handleView(action, recStoreId) {
    if (action === "add") {
      recordStoreId.value = genStoreId(_layoutSchedule.model, null);
    } else if (action === "return") {
      recordStoreId.value = null;
      dayViewRecordStoreId.value = null;
      setTimeData();
    } else if (action === "edit") {
      recordStoreId.value = recStoreId;
    } else {
      recordStoreId.value = null;
    }
  }

  function handleClose() {
    recordStoreId.value = null;
    dayViewRecordStoreId.value = null;
    setTimeData();
    viewMode.value = "calendar";
  }

  return (
    <div className="h-full w-full">
      <BoardForm
        layout={_layoutSchedule}
        storeId={recordStoreId.value}
        handleView={handleView}
        onClose={handleClose}
        closeOnChangeView
      />
    </div>
  );
}
